import React, { useEffect, useRef } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import moment from 'moment';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { isValidCountryCode, isValidPhoneNumberWithCountryCode } from 'utils/validators';
import TextInput from 'components/ui/TextInput';
import { Spinner } from 'admin-components/components';

import DiallingCodesSelector from '../../components/ui/DiallingCodesSelector';

import {
  StyledLabel,
  FormContainer,
  ErrorMessage,
  StyledInputFields,
  StyledInputWrapper,
  PassengerDetailsFieldsContainer
} from './BuyThalysPass.styles';
import { useCustomerDetails } from './BuyThalysPass.utils';

export const PassengerDetailsForm = ({ membershipNumber }) => {
  const formContext = useFormContext();

  const { register, formState, setValue, getValues, trigger } = formContext;
  const { errors } = formState;

  const customerDetails = useCustomerDetails(membershipNumber);
  const passengerPhoneCountryCodeRef = useRef(getValues('passengerPhoneCountryCode') ?? '');

  const disablePhoneNumber = !!customerDetails.data.passengerPhoneNumber && !!customerDetails.data.passengerPhoneCountryCode;
  const handleCountryCodeChange = (countryCode) => {
    passengerPhoneCountryCodeRef.current = countryCode ?? '';
    trigger('passengerPhoneNumber');
  };

  useEffect(() => {
    const { passengerPhoneCountryCode, passengerPhoneNumber, ...rest } = customerDetails.data;
    Object.entries(rest).forEach(([key, value]) => {
      setValue(key, value, { shouldValidate: true });
    });
    if (passengerPhoneCountryCode !== undefined) {
      passengerPhoneCountryCodeRef.current = passengerPhoneCountryCode ?? '';
    }
    setValue('passengerPhoneCountryCode', passengerPhoneCountryCode, { shouldValidate: true });
    setValue('passengerPhoneNumber', passengerPhoneNumber, { shouldValidate: true });
  }, [setValue, customerDetails.data]);

  if (customerDetails.loading) {
    return (
      <FormContainer>
        <Spinner />
      </FormContainer>
    );
  }

  return (
    <FormProvider {...formContext}>
      <FormContainer>
        <PassengerDetailsFieldsContainer>
          <StyledInputFields>
            <StyledLabel htmlFor="passengerFirstName">
              <FormattedMessage id="extrasOnlyBookings.fields.firstName" />
            </StyledLabel>
            <TextInput
              disabled={!!customerDetails.data.passengerFirstName}
              id="passengerFirstName"
              type="text"
              {...register('passengerFirstName', {
                required: true,
                pattern: /^([\D]|-){2,34}$/
              })}
            />
            {errors.passengerFirstName && (
              <ErrorMessage>
                <FormattedMessage id="extrasOnlyBookings.errors.firstName" />
              </ErrorMessage>
            )}
          </StyledInputFields>
          <StyledInputFields>
            <StyledLabel htmlFor="passengerLastName">
              <FormattedMessage id="extrasOnlyBookings.fields.lastName" />
            </StyledLabel>
            <TextInput
              disabled={!!customerDetails.data.passengerLastName}
              id="passengerLastName"
              type="text"
              {...register('passengerLastName', {
                required: true,
                pattern: /^([\D]|-){2,34}$/
              })}
            />
            {errors.passengerLastName && (
              <ErrorMessage>
                <FormattedMessage id="extrasOnlyBookings.errors.lastName" />
              </ErrorMessage>
            )}
          </StyledInputFields>
          <StyledInputFields>
            <StyledLabel htmlFor="passengerEmail">
              <FormattedMessage id="extrasOnlyBookings.fields.email" />
            </StyledLabel>
            <TextInput
              disabled={!!customerDetails.data.passengerEmail}
              id="passengerEmail"
              type="text"
              {...register('passengerEmail', {
                required: true,
                pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                minLength: 6,
                maxLength: 50
              })}
            />
            {errors.passengerEmail && (
              <ErrorMessage>
                <FormattedMessage id="extrasOnlyBookings.errors.email" />
              </ErrorMessage>
            )}
          </StyledInputFields>
          <StyledInputFields>
            <StyledLabel mandatory htmlFor="passengerDateOfBirth">
              <FormattedMessage id="paxForm.birthDate" />
            </StyledLabel>
            <StyledInputWrapper>
              <TextInput
                disabled={!!customerDetails.data.passengerDateOfBirth}
                type="date"
                {...register('passengerDateOfBirth', { required: true })}
                id="passengerDateOfBirth"
                max={moment().format('YYYY-MM-DD')}
                min="1900-01-01"
                style={{ height: '38px' }}
              />
              {errors.passengerDateOfBirth && (
                <ErrorMessage>
                  <FormattedMessage id="paxForm.validator.invalidBirthDate" />
                </ErrorMessage>
              )}
            </StyledInputWrapper>
          </StyledInputFields>
          <StyledInputFields style={{ maxWidth: '105px' }}>
            <StyledLabel mandatory htmlFor="passengerPhoneCountryCode">
              <FormattedMessage id="paxForm.countryCode" />
            </StyledLabel>
            <StyledInputWrapper>
              <DiallingCodesSelector
                disabled={disablePhoneNumber}
                required
                validate={isValidCountryCode}
                name="passengerPhoneCountryCode"
                id="passengerPhoneCountryCode"
                isInvalid={!isValidCountryCode}
                classes="StyledDiallingSelect"
                onChange={handleCountryCodeChange}
              />
            </StyledInputWrapper>
          </StyledInputFields>
          <StyledInputFields>
            <StyledLabel mandatory htmlFor="passengerPhoneNumber">
              <FormattedMessage id="extrasOnlyBookings.fields.phone" />
            </StyledLabel>
            <TextInput
              disabled={disablePhoneNumber}
              id="passengerPhoneNumber"
              type="text"
              {...register('passengerPhoneNumber', {
                required: true,
                validate: (value) => isValidPhoneNumberWithCountryCode(value, passengerPhoneCountryCodeRef.current)
              })}
            />
            {errors.passengerPhoneNumber && (
              <ErrorMessage>
                <FormattedMessage id="extrasOnlyBookings.errors.phone" />
              </ErrorMessage>
            )}
          </StyledInputFields>
        </PassengerDetailsFieldsContainer>
      </FormContainer>
    </FormProvider>
  );
};

PassengerDetailsForm.propTypes = {
  membershipNumber: PropTypes.string.isRequired
};
